import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { isEqual } from 'lodash';
import FormAccordionBase from '@/components/forms/new/FormAccordionBase';
import { LoadingIndicator } from '@/components/v4';
import { axios } from '@/services/axios';
import { CompanyFormProvider } from 'pages/organisations/companies/v5/context/CompanyFormContext';
import { useCompanyForm } from 'pages/organisations/companies/v5/hooks/useCompanyForm';
import walkthroughIds from '../../walkthroughIds';
import WholesalerGeneral from './tabs/WholesalerGeneral';
import WholesalerBillingInvoice from './tabs/WholesalerBillingInvoice';
import WholesalerCustomAttributes from './tabs/WholesalerCustomAttributes';
import WholesalerAdmin from './tabs/WholesalerAdmin';
import useCreateWholesaler from './queries/useCreateWholesaler';
import useGetWholesalerByID from './queries/useGetWholesalerByID';
import useUpdateWholesaler from './queries/useUpdateWholesaler';
import { getInitialFormData } from './utils/wholesalerFormUtils';

const WholesalerEditContent = ({
    initialData,
    mode,
    resetToMainTable,
    switchToEditMode,
    selectedWholesalerId,
}) => {
    const dispatch = useDispatch();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const { wholesalers: wholesalerWalkthroughIds } = walkthroughIds;

    const {
        formData,
        setFormData,
        setMode,
        initialFormData,
        setInitialFormData,
        deletedBillingNoteIDs,
    } = useCompanyForm();

    const [defaultPlans, setDefaultPlans] = useState([]);
    const isAddMode = mode === 'Add';
    const isEditMode = mode === 'Edit';

    const handleBackClick = () => {
        dispatch({ type: 'CHANGE_TABLE_LOADING' });
        resetToMainTable();
    };

    useEffect(() => {
        setMode('Wholesaler');
    }, [setMode]);

    useEffect(() => {
        if (isAddMode) {
            const initialFormData = getInitialFormData(initialData);
            setFormData(initialFormData);
            setInitialFormData(initialFormData);
        }
    }, [isAddMode, setFormData, initialData, setInitialFormData]);

    const { isLoading, isFetching } = useGetWholesalerByID(
        selectedWholesalerId,
        (data) => {
            const initialFormData = getInitialFormData(data);
            setFormData(initialFormData);
            setInitialFormData(initialFormData);
        },
        resetToMainTable,
        isEditMode,
    );

    const plansFetchId = isEditMode ? formData?.id : currentWholesaler;
    const { isLoading: isDefaultPlansLoading } = useQuery({
        queryKey: ['wholesalerDefaultPlans', plansFetchId, isAddMode],
        queryFn: async () => {
            if (!plansFetchId) return [];

            const response = await axios.get(
                `/wholesaler/${plansFetchId}/plans/available`,
            );
            const plans = response.data || [];
            setDefaultPlans(Array.isArray(plans) ? plans : []);
            return plans;
        },
        enabled: !!plansFetchId,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: 0,
        cacheTime: 0,
    });

    const defaultPlansData = {
        defaultPlans,
        isDefaultPlansLoading,
    };

    const hasFormErrors = (errors) => {
        if (!errors || typeof errors !== 'object') return false;

        return Object.values(errors).some((value) => {
            if (typeof value === 'object' && value !== null) {
                return hasFormErrors(value);
            }
            return Boolean(value);
        });
    };

    const hasFormChanged = (currentValues) => {
        if (!initialFormData || !currentValues?.formValues) return true;
        return !isEqual(initialFormData, currentValues.formValues);
    };

    const calculateIsSubmitDisabled = (errors, values) => {
        return hasFormErrors(errors) || !hasFormChanged(values);
    };

    const { mutate: createWholesaler } = useCreateWholesaler(
        formData,
        (createdWholesaler) => {
            if (switchToEditMode && createdWholesaler?.id) {
                switchToEditMode(createdWholesaler.id);
            } else {
                resetToMainTable();
            }
        },
    );

    const { mutate: updateWholesaler } = useUpdateWholesaler(
        formData,
        resetToMainTable,
        deletedBillingNoteIDs,
        initialFormData,
    );

    const breadCrumbTrail = [
        { label: 'Wholesalers', path: resetToMainTable },
        {
            label: isAddMode ? 'Add Wholesaler' : 'Edit Wholesaler',
            path: '',
        },
    ];

    const header = isAddMode
        ? 'Add Wholesaler'
        : `Edit ${formData?.name || 'Wholesaler'}`;

    const buildTabs = (errors, touched, values) => {
        const tabs = [
            {
                label: 'General',
                accordions: (
                    <WholesalerGeneral
                        key="wholesaler-general"
                        darkMode={darkMode}
                        errors={errors}
                        touched={touched}
                        values={values}
                        isAddMode={isAddMode}
                        defaultPlansData={defaultPlansData}
                    />
                ),
            },
        ];

        if (!isAddMode) {
            tabs.push(
                {
                    label: 'Billing & Invoice',
                    accordions: (
                        <WholesalerBillingInvoice
                            key="wholesaler-billing"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            defaultPlansData={defaultPlansData}
                        />
                    ),
                },
                {
                    label: 'Custom Fields',
                    accordions: [
                        <WholesalerCustomAttributes
                            key="wholesaler-customAttributes"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                    ],
                },
                {
                    label: 'Admin',
                    accordions: [
                        <WholesalerAdmin
                            key="wholesaler-admin"
                            darkMode={darkMode}
                            resetToMainTable={resetToMainTable}
                            errors={errors}
                            touched={touched}
                        />,
                    ],
                },
            );
        }

        return tabs;
    };

    if (((isLoading || isFetching) && isEditMode) || isDefaultPlansLoading) {
        return <LoadingIndicator />;
    }

    const finalFormSubmit = () => {
        isAddMode ? createWholesaler() : updateWholesaler();
    };

    return (
        <FormAccordionBase
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={formData}
            finalFormSubmit={finalFormSubmit}
            backLink={handleBackClick}
            submitButtonText={isAddMode ? 'Create' : 'Save'}
            isSubmitDisabled={calculateIsSubmitDisabled || true}
            ufBackBtn={wholesalerWalkthroughIds?.general?.ufBackBtn}
            ufSaveBtn={wholesalerWalkthroughIds?.general?.ufSaveBtn}
            enableReinitialize={true}
            enableTabs={true}
            defaultTabIndex={0}>
            {({ errors, touched, values }) =>
                buildTabs(errors, touched, values)
            }
        </FormAccordionBase>
    );
};

const WholesalerEdit = ({
    mode,
    resetToMainTable,
    switchToEditMode,
    selectedWholesalerId,
}) => {
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const initialData = {
        wholesalerID: currentWholesaler,
        wholesalerId: currentWholesaler,
    };

    return (
        <CompanyFormProvider initialData={getInitialFormData(initialData)}>
            <WholesalerEditContent
                initialData={initialData}
                mode={mode}
                resetToMainTable={resetToMainTable}
                switchToEditMode={switchToEditMode}
                selectedWholesalerId={selectedWholesalerId}
            />
        </CompanyFormProvider>
    );
};

export default WholesalerEdit;
